/**
 * Where a printed value came from. A reviewer reads a test record differently
 * when a condition was measured by the run than when someone typed it into
 * the report or the report guessed it from a step title, so a report carries
 * the source beside the value and the document marks every value whose source
 * is not the run.
 */

export type ValueSource = 'run' | 'template' | 'report' | 'inferred' | 'default';

export interface Sourced<T> {
  value: T;
  source: ValueSource;
  /** how it was found, e.g. "from the step title" or "nearest planned supply" */
  note?: string;
}

export const SOURCE_LABELS: Record<ValueSource, string> = {
  run: 'recorded by the run',
  template: 'from the report template',
  report: 'stated in the report',
  inferred: 'inferred',
  default: 'default',
};

export function sourced<T>(value: T, source: ValueSource, note?: string): Sourced<T> {
  return note ? { value, source, note } : { value, source };
}

const present = (value: unknown) => value !== null && value !== undefined && !(typeof value === 'string' && value.trim() === '');

/** The first candidate that holds a value, in the order given; null when none does. */
export function firstSourced<T>(...candidates: Array<Sourced<T | null | undefined>>): Sourced<T> | null {
  const found = candidates.find((c) => present(c.value));
  return found ? (found as Sourced<T>) : null;
}
